import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarOff, Loader2, Clock, CheckCircle2, XCircle, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { format } from "date-fns";
import { ar } from "date-fns/locale";

const leaveTypes: Record<string, string> = {
  sick: "مرضي",
  personal: "شخصي",
  family: "عائلي",
  travel: "سفر",
  other: "أخرى",
};

export default function LeaveRequestsManager() {
  const { user, profile } = useAuth();
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [adminNotes, setAdminNotes] = useState<Record<string, string>>({});
  const [processing, setProcessing] = useState<string | null>(null);

  useEffect(() => {
    fetchRequests();
  }, [profile?.nursery_id]);

  const fetchRequests = async () => {
    if (!profile?.nursery_id) return;
    setLoading(true);
    const { data } = await supabase
      .from("leave_requests")
      .select("*, children(full_name, classes(name))")
      .eq("nursery_id", profile.nursery_id)
      .order("created_at", { ascending: false });
    setRequests(data || []);
    setLoading(false);
  };

  const handleAction = async (id: string, status: "approved" | "rejected") => {
    if (!user) return;
    setProcessing(id);
    const { error } = await supabase
      .from("leave_requests")
      .update({
        status,
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
        admin_notes: adminNotes[id] || null,
      })
      .eq("id", id);

    setProcessing(null);
    if (error) {
      toast.error("خطأ: " + error.message);
    } else {
      toast.success(status === "approved" ? "تمت الموافقة على الطلب" : "تم رفض الطلب");
      setRequests((prev) => prev.map((r) => r.id === id ? { ...r, status, reviewed_at: new Date().toISOString(), admin_notes: adminNotes[id] || null } : r));
    }
  };

  const filtered = requests.filter((r) => filter === "all" ? true : r.status === filter);

  const counts = {
    pending: requests.filter((r) => r.status === "pending").length,
    approved: requests.filter((r) => r.status === "approved").length,
    rejected: requests.filter((r) => r.status === "rejected").length,
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "قيد المراجعة", count: counts.pending, icon: Clock, color: "text-amber-500", bg: "bg-amber-50" },
          { label: "موافق عليها", count: counts.approved, icon: CheckCircle2, color: "text-primary", bg: "bg-primary/5" },
          { label: "مرفوضة", count: counts.rejected, icon: XCircle, color: "text-destructive", bg: "bg-destructive/5" },
        ].map((s, i) => (
          <Card key={i} className="shadow-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center shrink-0`}>
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <div>
                <div className="text-xl font-bold text-foreground">{s.count}</div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter Tabs */}
      <Tabs value={filter} onValueChange={setFilter} dir="rtl">
        <TabsList>
          <TabsTrigger value="pending" className="gap-1">
            <Clock className="w-4 h-4" /> قيد المراجعة ({counts.pending})
          </TabsTrigger>
          <TabsTrigger value="approved" className="gap-1">
            <CheckCircle2 className="w-4 h-4" /> موافق عليها
          </TabsTrigger>
          <TabsTrigger value="rejected" className="gap-1">
            <XCircle className="w-4 h-4" /> مرفوضة
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-1">
            <FileText className="w-4 h-4" /> الكل
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Requests List */}
      <Card className="shadow-card">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="py-12 text-center">
              <CalendarOff className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-muted-foreground text-sm">لا توجد طلبات {filter === "pending" ? "قيد المراجعة" : ""}</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[500px]">
              <div className="divide-y">
                {filtered.map((req) => (
                  <div key={req.id} className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                        req.status === "approved" ? "bg-primary/10" : req.status === "rejected" ? "bg-destructive/10" : "bg-amber-50"
                      }`}>
                        {req.status === "approved" ? <CheckCircle2 className="w-4 h-4 text-primary" /> :
                         req.status === "rejected" ? <XCircle className="w-4 h-4 text-destructive" /> :
                         <Clock className="w-4 h-4 text-amber-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-foreground text-sm">{req.children?.full_name}</p>
                          {req.children?.classes?.name && (
                            <Badge variant="outline" className="text-[10px]">{req.children.classes.name}</Badge>
                          )}
                          <Badge variant="outline" className="text-[10px]">{leaveTypes[req.leave_type] || req.leave_type}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {format(new Date(req.start_date), "dd MMM", { locale: ar })} → {format(new Date(req.end_date), "dd MMM yyyy", { locale: ar })}
                        </p>
                        {req.reason && <p className="text-xs text-muted-foreground mt-1 bg-muted/50 rounded px-2 py-1">{req.reason}</p>}
                        <p className="text-[10px] text-muted-foreground/70 mt-1">
                          تقدم بالطلب: {format(new Date(req.created_at), "dd MMM yyyy HH:mm", { locale: ar })}
                        </p>
                      </div>
                    </div>

                    {req.status === "pending" && (
                      <div className="mr-12 space-y-2">
                        <Textarea
                          placeholder="ملاحظات للإدارة (اختياري)..."
                          rows={2}
                          value={adminNotes[req.id] || ""}
                          onChange={(e) => setAdminNotes((prev) => ({ ...prev, [req.id]: e.target.value }))}
                          className="text-sm"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="gradient-hero border-0"
                            disabled={processing === req.id}
                            onClick={() => handleAction(req.id, "approved")}
                          >
                            {processing === req.id ? <Loader2 className="w-3 h-3 animate-spin ml-1" /> : <CheckCircle2 className="w-3 h-3 ml-1" />}
                            موافقة
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={processing === req.id}
                            onClick={() => handleAction(req.id, "rejected")}
                          >
                            <XCircle className="w-3 h-3 ml-1" />
                            رفض
                          </Button>
                        </div>
                      </div>
                    )}

                    {req.admin_notes && req.status !== "pending" && (
                      <div className="mr-12">
                        <p className="text-xs text-primary bg-primary/5 rounded px-2 py-1">ملاحظات: {req.admin_notes}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
